import { OrderItem } from "@/types";
import { getWithPagination, http, httpForm } from "../utils/http-common";
import { useOrdersStore } from "../store/orders.store";

export function getAllOrderItems(parameters?: string) {
  return getWithPagination("/orderItems" + parameters || "").then((res) => {
    return res;
  });
}
export function getOrderItems(parameters?: string) {
  return http.get("/orderItems" + parameters || "").then((res) => {
    return res.data;
  });
}
export const DASHBOARD_ORDER_ITEMS_LIMIT = 500;

export function getDashboardOrderItems(dateFrom: Date, dateTo: Date) {
  const params = new URLSearchParams();
  params.set("createdAt__between", `${dateFrom.toISOString()},${dateTo.toISOString()}`);
  params.set("order", "-createdAt");
  params.set("limit", String(DASHBOARD_ORDER_ITEMS_LIMIT));
  params.set("page", "1");
  return http.get(`/orderItems/dashboard?${params.toString()}`).then((res) => {
    return {
      data: res.data.data ?? [],
      total: res.data.total ?? 0,
    };
  });
}

export function getOrderItemsWithInfo(
  stageId?: number,
  limit?: number,
  page?: number,
  sortField?: string,
  sortOrder?: 0 | 1 | -1 | null,
  filters?: string,
  mode: "list" | "full" | "core" = "list",
  hydrate?: { includes?: string[]; orderCustomFieldIds?: number[]; orderItemCustomFieldIds?: number[] },
) {
  const params = new URLSearchParams();
  if (stageId) params.append("workflowStageId", stageId.toString());
  if (limit) params.append("limit", limit.toString());
  if (page) params.append("page", page.toString());
  const orderDirection = sortOrder && sortOrder >= 0 ? "+" : "-";
  const order = `${orderDirection}${sortField || "createdAt"}`;
  params.append("order", order);
  if (mode === "list" || mode === "core") params.append("mode", mode);
  if (hydrate?.includes?.length) params.append("includes", hydrate.includes.join(","));
  if (hydrate?.orderCustomFieldIds?.length) params.append("orderCustomFieldIds", hydrate.orderCustomFieldIds.join(","));
  if (hydrate?.orderItemCustomFieldIds?.length) params.append("orderItemCustomFieldIds", hydrate.orderItemCustomFieldIds.join(","));
  return http.get(`/orderItems/withInfo?${filters ? filters : ""}`, { params }).then((res) => {
    return res.data;
  });
}

export type ExportOrderItemsExcelPayload = {
  groupBy: string;
  columns: {
    field: string;
    header: string;
    type?: string;
    customFieldId?: number;
    entity?: string;
    isJobColumn?: boolean;
  }[];
  ids?: number[];
  filters?: string;
  workflowStageId?: number;
  filename?: string;
};

export async function exportOrderItemsExcel(payload: ExportOrderItemsExcelPayload): Promise<void> {
  const { filename, ...body } = payload;
  const response = await http.post("/orderItems/export/excel", body, { responseType: "blob" });
  const url = window.URL.createObjectURL(
    new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename || "order-items.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
export function countItems(stageId?: number, filters?: string) {
  const params = new URLSearchParams();
  if (stageId) params.append("workflowStageId", stageId.toString());
  return http.get(`/orderItems/count?${filters ? filters : ""}`, { params }).then((res) => {
    return res.data.count;
  });
}

export function getOrderItemsWithMaterial() {
  return http.get("/orderItems?with=material").then((res) => {
    return res.data;
  });
}

export function getOrderItemsFromAllCompanies(from: Date, to: Date, superAdminToken?: string) {
  return http.get(`/orderItems/allCompanies?from=${from.getTime()}&to=${to.getTime()}`, { headers: { superAdminToken: superAdminToken ?? "" } }).then((res) => {
    return res.data;
  });
}

export function getSingleOrderItem(id: number, parameters?: string): Promise<OrderItem> {
  return http.get(`/orderItems/${id}` + (parameters ?? "")).then((res) => {
    return res.data.data;
  });
}
export async function deleteOrderItem(orderItemId: number): Promise<void> {
  const ordersStore = useOrdersStore();
  ordersStore.deleteOrderItemByStage(orderItemId);
  return http.delete(`/orderItems/${orderItemId}`);
}
export async function getOrderItemsForWorkflowStage(workflowStageId: number) {
  return http.get(`/orderItems?with=order,receivers,children,children.order,currentFile&limit=50&workflowStageId=${workflowStageId}`).then((res) => {
    return res.data.data;
  });
}
export async function createOrderItem(orderItem: Partial<OrderItem>) {
  return http.post(`/orderItems`, orderItem).then((res) => {
    return res.data.data;
  });
}

export function editOrderItem(orderItem: OrderItem | Pick<OrderItem, "position" | "workflowStageId"> | Pick<OrderItem, "workflowStageId">, id: number) {
  return http.put(`/orderItems/${id}`, orderItem).then((res) => {
    return res.data.data;
  });
}

export async function editBulkItems(data: Partial<OrderItem>[]) {
  return http.put(`/orderItems/bulk?`, data).then((res) => {
    return res.data.data;
  });
}

export async function editBulkItemsByIds(ids: number[], data: Partial<OrderItem>) {
  return http.put(`/orderItems/bulkByIds?ids=${ids.join(",")}`, data).then((res) => {
    return res.data.data;
  });
}

export async function getIncomingOrderItems() {
  const items = await http.get(`/orderItems/incoming`);
  return items.data.data;
}

export function replaceItemFile(orderItem: OrderItem, onProgress?: any) {
  const formData = new FormData();
  formData.append("orderItem", JSON.stringify(orderItem));
  formData.append("file", orderItem.uploadedFile);

  const config = {
    onUploadProgress: function (progressEvent: any) {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      console.log(percentCompleted);
      if (onProgress) onProgress(percentCompleted);
    },
  };
  return httpForm.putForm(`/orderItems/replaceFile/${orderItem.id}`, formData, config).then((res) => {
    return res.data.data;
  });
}

export function getThumbnailURL(item: OrderItem): Promise<string> {
  return http.get(`/orderItems/thumbnail/${item.id}`).then((res) => {
    return res.data;
  });
}

export function getFilesForOrderItem(orderId: number) {
  return http.get(`/orderItems/files/${orderId}`).then((res) => {
    return res.data.data;
  });
}

export function checkIfFileExists(orderItemId: number | undefined, type: string) {
  if (!orderItemId || orderItemId < 0) return Promise.resolve(false);
  return http.get(`/orderItems/${orderItemId}/fileExists?type=${type}`).then((res) => {
    return res.data;
  });
}
export async function sendOrderItemToPartner(orderItemId: number, receiverId: string, stageId: number) {
  return http.get(`/orderItems/${orderItemId}/send/${receiverId}?moveToStage=${stageId}`);
}

export async function sendOrderItemsToPartnerBulk(orderItemIds: number[], receiverId: string, stageId?: number) {
  return http
    .post(`/orderItems/send/batch`, {
      orderItemIds,
      receiverId,
      moveToStage: stageId,
    })
    .then((res) => {
      return res.data;
    });
}

export async function removeOrderItemReceiver(orderItemId: number, receiverId: string, stageId?: number) {
  const query = stageId ? `?moveToStage=${stageId}` : "";
  return http.delete(`/orderItems/${orderItemId}/removeReceiver/${receiverId}${query}`);
}

export async function rejectOrderItem(orderItemId: number, reason: string) {
  return http.put(`/orderItems/${orderItemId}/reject`, { reason: reason });
}

export async function deleteOrderItemsBulk(orderItemIds: number[]) {
  return http.delete(`/orderItems/bulk`, { params: { ids: orderItemIds.join(",") } }).then((res) => {
    return res.data;
  });
}

export async function getAllStatuses() {
  return http.get("/orderItems/statusesList").then((res) => {
    return res.data.data;
  });
}

export async function moveItemToNextStage(orderItemId: number | undefined) {
  if (!orderItemId) return;
  return http.post(`/orderItems/nextStage?id=${orderItemId}`).then((res) => {
    return res.data.data;
  });
}
// export async function createItems(orderId: number, orderItems: OrderItem[], _onProgress: any) {
//   const results: Promise<OrderItem>[] = [];

//   for (const item of orderItems) {
//     if (item.isNew) {
//       item.orderId = orderId;
//       // eslint-disable-next-line @typescript-eslint/no-unused-vars
//       const { isNew, file, uploadedFile, ...rest } = item;

//       results.push(
//         http.post(`/orderItems`, rest).then((res) => {
//           const orderItem = res.data.data as OrderItem;
//           if (orderItem && orderItem.id && item.uploadedFile) {
//             uploadFiles(orderItem.id, item.uploadedFile, _onProgress);
//           }
//           return orderItem;
//         })
//       );
//     }
//   }
//   await Promise.all(results);
// }
